import produce from "immer";
import { createAction } from "lib/actionCreator";
import {
	CursorPageResponseData,
	CursorResults,
} from "lib/api/services/cursors";
import {
	Clause,
	Condition,
	ConditionMap,
	QueryByFormInputTypes,
	QueryClause,
	QueryStatement,
} from "lib/api/types";

export function createFilter<N extends string, O extends keyof ConditionMap>(
	id: N,
	operator: O,
	value: ConditionMap[O]
) {
	return {
		[id]: {
			[operator]: value,
		},
	} as { [P in N]: Condition<O> };
}

export type SearchField = QueryByFormInputTypes;
export type SearchFields = { [k: string]: SearchField };

export interface FinderState {
	searchFields: SearchFields;
	activeSearchFields: string[];
	query: QueryClause;
	querySummary: string[];
	results?: CursorResults;
	resultsHasMore: boolean;
	selectedRecords: string[];
	cursorId?: string;
}

export const defaultState = (state: Partial<FinderState>): FinderState => ({
	searchFields: {},
	activeSearchFields: [],
	query: [],
	resultsHasMore: false,
	selectedRecords: [],
	querySummary: [],
	...state,
});

// Action types
const SET_FIELD_VALUE = "SET_FIELD_VALUE";
const REMOVE_FIELD = "REMOVE_FIELD";
const SET_QUERY_VALUE = "SET_QUERY_VALUE";
const SET_SEARCH_RESULTS = "SET_SEARCH_RESULTS";
const ADD_SEARCH_RESULTS = "ADD_SEARCH_RESULTS";
const ADD_SELECTED = "ADD_SELECTED";
const CLEAR_SELECTED = "CLEAR_SELECTED";
const SET_NEW_STATE = "SET_NEW_STATE";

/**
 * Set a field to be search in the search form
 *
 * @param {string} value the new field value
 * @param {number=} index index of the field
 */
export const setFieldValue = (name: string, index?: number) =>
	createAction(SET_FIELD_VALUE, { name, index });

/**
 * Remove a field from the search form
 *
 * @param {number} index the index of the search field to be removed from the search form
 */
export const removeField = (index: number) =>
	createAction(REMOVE_FIELD, { index });

/**
 * Update a query clause for an active search field
 * @param query filter to be added to the search query
 * @param index index of the search field
 * @param summary summary of the search value for the "search criteria"
 */
export const setQueryFilter = (
	clause: QueryStatement | Clause,
	index: number,
	summary: string
) => createAction(SET_QUERY_VALUE, { index, clause, summary });

/**
 * Set the search results, this will override any existing results
 * @param results a set of seach results
 */
export const setSearchResults = (data: CursorPageResponseData) =>
	createAction(SET_SEARCH_RESULTS, data);

/**
 * Add search results to the existing results - this is for pagination
 * @param results a set of search results
 */
export const addSearchResults = (data: CursorPageResponseData) =>
	createAction(ADD_SEARCH_RESULTS, data);

/**
 * Add a record ID to the selected records array
 * @param id the id of the record to add
 */
export const addSelected = (ids: string[]) =>
	createAction(ADD_SELECTED, { ids });

/**
 * Clear the selected records array
 */
export const clearSelected = () => createAction(CLEAR_SELECTED);

/**
 * Set new state
 */

export const setNewState = (data: Partial<FinderState>) =>
	createAction(SET_NEW_STATE, data);
/**
 * Reducer action types
 */
export type Actions = ReturnType<
	| typeof setFieldValue
	| typeof removeField
	| typeof setQueryFilter
	| typeof addSearchResults
	| typeof setSearchResults
	| typeof addSelected
	| typeof clearSelected
	| typeof setNewState
>;

/**
 * Reducer for the Finder modal that contains the query by form search
 * interface and search results grid
 *
 * @param state Finder state
 * @param action Finder reducer actions
 */
function finderReducer(state: FinderState, action: Actions): FinderState {
	return produce(state, draft => {
		switch (action.type) {
			case SET_FIELD_VALUE: {
				// If we have an index the search field already exists, so
				// update it
				if (action.payload.index !== undefined) {
					draft.activeSearchFields[action.payload.index] =
						action.payload.name;
					// Remove query clause when updating the search field
					draft.query.splice(action.payload.index, 1);
				}
				// If we have no index, the search field is being added,
				// push it into our activeSearchFields array
				else {
					draft.activeSearchFields.push(action.payload.name);
				}
				break;
			}
			case REMOVE_FIELD: {
				// Remove the search field
				draft.activeSearchFields.splice(action.payload.index, 1);
				// Remove the corresponding query clause
				draft.query.splice(action.payload.index, 1);
				// and the summary data
				draft.querySummary.splice(action.payload.index, 1);
				break;
			}
			case SET_QUERY_VALUE: {
				// Updates a query clause
				draft.query[action.payload.index] = action.payload.clause;
				draft.querySummary[action.payload.index] =
					action.payload.summary;
				break;
			}
			case SET_SEARCH_RESULTS: {
				// Set results to the new set - overrides any existing results
				draft.results = action.payload.results;
				draft.resultsHasMore = action.payload.hasMore;
				draft.cursorId = action.payload.cursorId;
				break;
			}
			case ADD_SEARCH_RESULTS: {
				// Add to the results if they already exists or just set results
				draft.results = draft.results
					? [...draft.results, ...action.payload.results]
					: action.payload.results;
				draft.resultsHasMore = action.payload.hasMore;
				draft.cursorId = action.payload.cursorId;
				break;
			}

			case ADD_SELECTED: {
				draft.selectedRecords = action.payload.ids;
				break;
			}

			case CLEAR_SELECTED: {
				draft.selectedRecords = [];
				break;
			}

			case SET_NEW_STATE: {
				const tempState: FinderState = {
					searchFields: {},
					activeSearchFields: [],
					query: [],
					resultsHasMore: false,
					selectedRecords: [],
					querySummary: [],
					...action.payload,
				};
				draft.searchFields = tempState.searchFields;
				draft.activeSearchFields = tempState.activeSearchFields;
				draft.query = tempState.query;
				draft.resultsHasMore = tempState.resultsHasMore;
				draft.selectedRecords = tempState.selectedRecords;
				draft.querySummary = tempState.querySummary;
				draft.results = tempState.results;
				draft.cursorId = tempState.cursorId;
				break;
			}
		}
	});
}

export default finderReducer;
